import React, { useState, useEffect, useRef } from 'react';
import { useTheme, Theme } from '../../contexts/ThemeContext';
import './ThemeToggle.css';

export const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const themes: { value: Theme; label: string; icon: string }[] = [
    { value: 'light', label: 'Light', icon: '☀️' },
    { value: 'dark', label: 'Dark', icon: '🌙' },
    { value: 'auto', label: 'Auto', icon: '🔄' }
  ];

  const currentTheme = themes.find(t => t.value === theme);

  return (
    <div className="theme-toggle">
      <button
        className="theme-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Current theme: ${currentTheme?.label}. Click to change theme.`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <span className="theme-icon">{currentTheme?.icon}</span>
        <span className="theme-label">{currentTheme?.label}</span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <ul className="theme-dropdown" role="menu">
          {themes.map(({ value, label, icon }) => (
            <li key={value} role="none">
              <button
                className={`theme-option ${theme === value ? 'active' : ''}`}
                onClick={() => {
                  setTheme(value);
                  setIsOpen(false);
                }}
                role="menuitem"
                aria-label={`Switch to ${label} theme`}
              >
                <span className="theme-option-icon">{icon}</span>
                <span className="theme-option-label">{label}</span>
                {theme === value && <span className="checkmark">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};